/**
 * Centralized structured log context factory.
 *
 * Every log statement should use this to attach `feature` + `workflow` metadata
 * for Datadog filtering. One function, one import — no per-feature factory files.
 *
 * Fields:
 *   feature  — domain area (chat, video-call, transcription)
 *   workflow  — specific operation within the feature (connection, get-token)
 *   detail   — (optional) human-readable explanation of why this event was
 *              emitted and what it represents. Queryable in Datadog as @detail.
 *   ...attrs — contextual IDs and values (patientId, roomName, etc.)
 *
 * Usage:
 *   // Worker (nestjs-pino)
 *   this.logger.log(
 *     logContext('transcription', 'transcribe-recording', { roomName }),
 *     'Starting transcription',
 *   );
 *
 *   // Frontend (Datadog Browser Logs) — with detail
 *   datadogLogs.logger.info(
 *     'Chat client connected',
 *     logContext('chat', 'connection', {
 *       detail: 'WebSocket connected, initial unread count seeded from server',
 *       chatUserId,
 *       initialUnreadCount,
 *     }),
 *   );
 *
 * Datadog queries:
 *   @feature:transcription @workflow:transcribe-recording
 *   @feature:chat @workflow:connection @detail:*unread*
 */

// ─── Feature → Workflow registry ────────────────────────────────────────
// Add features and their workflows here. Known features get autocomplete
// and typo protection. Unknown features still work (accepts any string)
// so teams can adopt gradually without blocking on registry updates.

interface FeatureWorkflows {
  'provider-events': 'connection' | 'delivery' | 'refresh';
  // Worker features
  transcription:
    | 'transcribe-recording'
    | 'generate-transcript'
    | 'eject-participant'
    | 'get-recording-ids'
    | 'merge-transcripts'
    | 'poll-job'
    | 'get-transcript'
    | 'fanout-jobs'
    | 'submit-soap-generation'
    | 'submit-scoring'
    | 'submit-event-detection';
  'soap-note':
    | 'generate'
    | 'ai-generation'
    | 'database-update'
    | 'legacy-fallback'
    | 'update-healthie-status';
  'sqs-consumer':
    | 'handle-message'
    | 'process-message'
    | 'apply-backoff'
    | 'parse-body';
  'session-recap': 'generate' | 'invoke-ai' | 'track-analytics';
  'session-prep': 'walmart-documents' | 'walmart-pdf-loaded';
  'provider-scoring':
    | 'score'
    | 'grade-transcript'
    | 'friendly-feedback'
    | 'track-analytics';
  'event-detection': 'detect' | 'send-transcript' | 'track-analytics';
  appointment:
    | 'fetch-from-healthie'
    | 'resolve-patient'
    | 'no-show-cascade'
    | 'read-payer-policies';
  availability:
    | 'create-availability'
    | 'edit-availability'
    | 'delete-availability'
    | 'list-availability'
    | 'check-reminder'
    | 'reminder-update-click'
    | 'resolve-profile'
    | 'validation-blocked';

  // Frontend features (for future migration from per-feature factories)
  'video-call':
    | 'room-verification'
    | 'call-join'
    | 'call-leave'
    | 'call-error'
    | 'auto-recording'
    | 'waiting-room'
    | 'end-appointment'
    | 'transcription'
    | 'dial-out'
    | 'duration-tracking'
    | 'session-beacon'
    | 'patient-join'
    | 'tray-buttons'
    | 'save-appointment';
  scheduling:
    | 'get-upcoming'
    | 'get-available-slots'
    | 'get-appointment-types'
    | 'booking'
    | 'booking-error'
    | 'reschedule'
    | 'reschedule-error'
    | 'reschedule-rollback'
    | 'reschedule-rollback-error'
    | 'restore-status'
    | 'drawer-open';
  m2m: 'token-verification' | 'transcript-access' | 'auth-failure';
  'meal-plan':
    | 'send-to-patient-app'
    | 'send-to-patient-app-error'
    | 'upload-to-healthie'
    | 'upload-to-healthie-retry'
    | 'upload-to-healthie-error'
    | 'chat-message'
    | 'chat-message-retry'
    | 'chat-message-error'
    | 'pdf-generation'
    | 'pdf-generation-error'
    | 'shopping-list'
    | 'shopping-list-error'
    | 'send-complete'
    | 'send-partial'
    | 'send-failed'
    | 'generate-instructions'
    | 'generate-instructions-skip'
    | 'generate-instructions-force'
    | 'resolve-transcript'
    | 'ai-generate-instructions'
    | 'healthie-patient-lookup'
    | 'generate-summary'
    | 'generate-detailed-plan';
  chat:
    | 'connection'
    | 'connection-error'
    | 'disconnection'
    | 'get-token'
    | 'get-token-error'
    | 'get-channel'
    | 'get-channel-error'
    | 'error-boundary';
  'benefit-check': 'icd-codes-source';
  events:
    | 'list-events'
    | 'get-event'
    | 'update-status'
    | 'bulk-update-status'
    | 'get-count'
    | 'fetch-provider'
    | 'fetch-patient'
    | 'glp-confusion-check';
  'api-client':
    | 'token-refresh'
    | 'interceptor-retry'
    | 'interceptor-diag'
    | 'interceptor-max-retries';
  'session-note': 'save' | 'submit-with-ai';
  'session-reminders': 'popup-shown';
  'journal-daily-totals': 'fetch-entries' | 'compute';
  'patient-registry': 'resolve-registry-id';
  'prescription-review-v2':
    | 'list-patients'
    | 'prior-auth-submit'
    | 'not-prescribed-submit'
    | 'not-prescribed-clear'
    | 'prescriber-notes-set'
    | 'decision-modal-open'
    | 'prescribe-click'
    | 'photon-prescribe-fallback-click'
    | 'photon-prescribe-escape-sso'
    | 'photon-embed-load'
    | 'photon-prescribe-success'
    | 'photon-prescriber-note'
    | 'photon-signer-check'
    | 'patient-drawer-open'
    | 'pa-diagnosis-labels'
    | 'document-request-created'
    | 'document-request-updated'
    | 'document-request-closed'
    | 'document-request-file-open'
    | 'opened-document-record'
    | 'unread-labs'
    | 'lab-file-open';
  'prescription-tasks':
    | 'list'
    | 'get-modal-context'
    | 'prior-auth-submit'
    | 'prior-auth-submit-failed'
    | 'complete-task'
    | 'complete-task-failed'
    | 'modal-open'
    | 'prescribe-click'
    | 'photon-prescribe-fallback-click'
    | 'photon-prescribe-escape-sso'
    | 'photon-embed-load'
    | 'photon-prescribe-success'
    | 'photon-prescriber-note'
    | 'photon-signer-check';
  'in-session-templates': 'resolve-context' | 'list-for-session';
  'attestation-documents': 'list' | 'file-access' | 'attest';
  'support-concierge':
    | 'prepareAttachment'
    | 'inbox-view'
    | 'conversation-open'
    | 'refresh-click'
    | 'unreadConversations'
    | 'markRead'
    | 'availability'
    | 'conversations'
    | 'conversation'
    | 'create'
    | 'reply'
    | 'resolve';
  'medicare-bridge': 'get-case' | 'submit-review' | 'clinical-prefill';
  'platform-api': 'assertion-mint';
  'nutrient-goals': 'read' | 'save';
  growthbook: 'evaluate';
}

// ─── Public API ─────────────────────────────────────────────────────────

export interface LogContext {
  feature: string;
  workflow: string;
  /** Human-readable explanation of why this event was emitted and what it represents. */
  detail?: string;
  [key: string]: unknown;
}

/**
 * Build a structured log context.
 *
 * Known features (registered in FeatureWorkflows) get workflow autocomplete.
 * Unknown features accept any workflow string — lets teams adopt without
 * needing to update the registry first.
 *
 * Include `detail` in attrs to explain what the event represents — it becomes
 * a queryable `@detail` attribute in Datadog.
 */
export function logContext<F extends string>(
  feature: F,
  workflow: F extends keyof FeatureWorkflows ? FeatureWorkflows[F] : string,
  attrs?: { detail?: string } & Record<string, unknown>,
): LogContext {
  return { feature, workflow, ...attrs };
}
